import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Flame, Send, Trophy, Calendar, CheckCircle,
  Mic, FileText, Volume2, Share2, Globe, Lock
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import VoiceRecorder from "@/components/VoiceRecorder";
import api from "@/lib/api";
import { toast } from "sonner";

export default function DailyPromptsPage() {
  const [prompt, setPrompt] = useState(null);
  const [streak, setStreak] = useState({ streak: 0, total_responses: 0, level: "Newcomer" });
  const [response, setResponse] = useState("");
  const [responseMode, setResponseMode] = useState("text"); // "text" | "voice"
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(null); // response_id or null
  const [shareAnonymous, setShareAnonymous] = useState(false);

  const loadAll = async () => {
    try {
      const [pRes, sRes, hRes] = await Promise.all([
        api.get("/prompts/daily"),
        api.get("/prompts/streak"),
        api.get("/prompts/history"),
      ]);
      setPrompt(pRes.data);
      setStreak(sRes.data);
      setHistory(hRes.data.responses);
    } catch {
      toast.error("Failed to load prompts");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleTextSubmit = async () => {
    if (!response.trim()) { toast.error("Write something first!"); return; }
    setSubmitting(true);
    try {
      await api.post("/prompts/respond", {
        prompt_text: prompt.prompt,
        response_type: "text",
        content: response,
      });
      toast.success("Response saved! Keep the streak going!");
      setResponse("");
      await loadAll();
    } catch {
      toast.error("Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoiceSubmit = async (blob) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "prompt_response.webm");
      formData.append("prompt_text", prompt.prompt);
      const res = await api.post("/prompts/respond/voice", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Voice response saved! Transcription: "${res.data.transcription?.slice(0, 60)}..."`);
      await loadAll();
    } catch {
      toast.error("Failed to submit voice response");
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async (responseId, isShared) => {
    try {
      if (isShared) {
        // Find the share_id and unshare
        const item = history.find(h => h.response_id === responseId);
        if (item?.share_id) {
          await api.delete(`/community/share/${item.share_id}`);
          toast.success("Removed from community feed");
        }
        await loadAll();
      } else {
        // Open share modal to choose anonymous or not
        setShareModalOpen(responseId);
        setShareAnonymous(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update share status");
    }
  };

  const confirmShare = async () => {
    if (!shareModalOpen) return;
    try {
      await api.post("/community/share", { 
        response_id: shareModalOpen, 
        is_anonymous: shareAnonymous 
      });
      toast.success(shareAnonymous ? "Shared anonymously!" : "Shared to community!");
      setShareModalOpen(null);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to share");
    }
  };

  const levelColors = {
    "Newcomer": "text-slate-400",
    "Explorer": "text-blue-400",
    "Storyteller": "text-purple-400",
    "Legacy Keeper": "text-[#00FF88]",
  };

  // Level progress
  const levelThresholds = { "Newcomer": 5, "Explorer": 15, "Storyteller": 30, "Legacy Keeper": 999 };
  const currentThreshold = levelThresholds[streak.level] || 5;
  const prevThreshold = streak.level === "Explorer" ? 5 : streak.level === "Storyteller" ? 15 : streak.level === "Legacy Keeper" ? 30 : 0;
  const progress = Math.min(100, ((streak.total_responses - prevThreshold) / (currentThreshold - prevThreshold)) * 100);

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="daily-prompts-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Daily Prompts</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Reflect & Remember
        </h1>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
          <Flame className="w-5 h-5 text-orange-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }} data-testid="prompt-streak">{streak.streak}</p>
          <p className="text-xs text-slate-500 mt-1">Day Streak</p>
        </div>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
          <Calendar className="w-5 h-5 text-blue-400 mx-auto mb-2" />
          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }} data-testid="prompt-total">{streak.total_responses}</p>
          <p className="text-xs text-slate-500 mt-1">Total Entries</p>
        </div>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 text-center">
          <Trophy className="w-5 h-5 text-[#00FF88] mx-auto mb-2" />
          <p className={`text-base font-bold ${levelColors[streak.level] || "text-white"}`} style={{ fontFamily: 'Manrope' }} data-testid="prompt-level">{streak.level}</p>
          <div className="w-full bg-white/5 rounded-full h-1.5 mt-2">
            <div className="bg-[#00FF88] h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[10px] text-slate-600 mt-1">{streak.total_responses}/{currentThreshold}</p>
        </div>
      </div>

      {/* Today's Prompt */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="rounded-2xl p-8 md:p-10 border border-[#00FF88]/10" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111 100%)' }}>
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-[#00FF88]" />
            <span className="text-xs tracking-[0.2em] uppercase text-[#00FF88] font-medium">
              Prompt {(prompt?.prompts_answered_today || 0) + 1} of {prompt?.total_prompts_available || 70}
            </span>
          </div>
          <p className="text-2xl md:text-3xl font-semibold text-white leading-snug mb-8" style={{ fontFamily: 'Manrope' }} data-testid="prompt-question">
            {prompt?.prompt || "Loading..."}
          </p>

          <div className="space-y-4">
              {/* Response mode toggle */}
              <div className="flex gap-2 glass-card rounded-xl p-1.5 w-fit">
                <button
                  onClick={() => setResponseMode("text")}
                  data-testid="mode-text-btn"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    responseMode === "text" ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" /> Write
                </button>
                <button
                  onClick={() => setResponseMode("voice")}
                  data-testid="mode-voice-btn"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    responseMode === "voice" ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"
                  }`}
                >
                  <Mic className="w-3.5 h-3.5" /> Speak
                </button>
              </div>

              {responseMode === "text" ? (
                <>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Write your response..."
                    data-testid="prompt-response-input"
                    className="w-full bg-white/5 border border-white/10 focus:border-[#00FF88]/30 text-white placeholder:text-white/20 rounded-xl p-4 h-36 resize-none outline-none focus:ring-1 focus:ring-[#00FF88]/30"
                  />
                  <Button
                    onClick={handleTextSubmit}
                    disabled={submitting || !response.trim()}
                    data-testid="prompt-submit-btn"
                    className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-8 py-6 font-bold shadow-[0_0_20px_rgba(0,255,136,0.3)]"
                  >
                    {submitting ? "Submitting..." : "Submit Response"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </>
              ) : (
                <div className="bg-white/5 border border-white/10 rounded-xl p-8 flex flex-col items-center gap-4">
                  <p className="text-sm text-slate-400">Press to record your voice response</p>
                  <VoiceRecorder onRecordingComplete={handleVoiceSubmit} disabled={submitting} />
                  {submitting && (
                    <p className="text-sm text-[#00FF88] animate-pulse">Transcribing & saving...</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-4 uppercase tracking-wider">Past Reflections</h3>
          <div className="space-y-4">
            {history.map((r, i) => (
              <motion.div
                key={r.response_id || i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6"
                data-testid={`history-entry-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-xs text-[#00FF88] font-medium flex-1">{r.prompt_text}</p>
                  {r.response_type === "voice" && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full">
                      <Mic className="w-2.5 h-2.5" /> Voice
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{r.content}</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                  <p className="text-xs text-slate-600">{r.date}</p>
                  <button
                    onClick={() => handleShare(r.response_id, r.is_shared)}
                    data-testid={`share-btn-${i}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      r.is_shared
                        ? "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20"
                        : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {r.is_shared ? (
                      <>
                        <Globe className="w-3 h-3" /> {r.is_anonymous ? "Shared Anonymously" : "Shared"}
                      </>
                    ) : (
                      <>
                        <Share2 className="w-3 h-3" /> Share to Community
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShareModalOpen(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.1] rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              Share to Community
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              Your reflection will be visible to other community members. Would you like to share anonymously?
            </p>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => setShareAnonymous(false)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
                  !shareAnonymous
                    ? "border-[#00FF88]/50 bg-[#00FF88]/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${!shareAnonymous ? "bg-[#00FF88]/20" : "bg-white/5"}`}>
                  <Globe className={`w-5 h-5 ${!shareAnonymous ? "text-[#00FF88]" : "text-slate-400"}`} />
                </div>
                <div className="text-left">
                  <p className={`text-sm font-medium ${!shareAnonymous ? "text-[#00FF88]" : "text-white"}`}>Share with my name</p>
                  <p className="text-xs text-slate-500">Others will see your name</p>
                </div>
              </button>

              <button
                onClick={() => setShareAnonymous(true)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
                  shareAnonymous
                    ? "border-purple-500/50 bg-purple-500/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${shareAnonymous ? "bg-purple-500/20" : "bg-white/5"}`}>
                  <Lock className={`w-5 h-5 ${shareAnonymous ? "text-purple-400" : "text-slate-400"}`} />
                </div>
                <div className="text-left">
                  <p className={`text-sm font-medium ${shareAnonymous ? "text-purple-400" : "text-white"}`}>Share anonymously</p>
                  <p className="text-xs text-slate-500">Your name will be hidden</p>
                </div>
              </button>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShareModalOpen(null)}
                variant="outline"
                className="flex-1 border-white/10 text-slate-400 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmShare}
                className="flex-1 bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full"
              >
                Share Now
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
